/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import {
  CheckOutlined,
  EditOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
} from '@ant-design/icons'
import type { InputRef } from 'antd'
import { useFormikContext } from 'formik'
import { useEffect, useRef, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  buildPluginCfg,
  buildPluginPreviewUrl,
  pluginPreviewDefaultOrigin,
  type PluginEmbedSettings,
  type PluginKey,
} from '@sportninja/common/features/account/apiAccess'
import Button from 'src/components/ts-components/Button'
import EmptyState from 'src/components/ts-components/EmptyState'
import { SkeletonRow } from 'src/components/ts-components/SkeletonBlock'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import {
  PLUGIN_KEYS_FAILED_MESSAGE,
  PLUGIN_PREVIEW_DEBOUNCE_MS,
  PLUGIN_PREVIEW_HEIGHT,
  PLUGIN_PREVIEW_MESSAGES,
  WEBSITE_PLUGIN_CARDS,
} from '../constants'
import { useApiBaseUrl } from '../hooks/useApiBaseUrl'
import { ApiAccessCard } from './ApiAccessCard'
import { PluginKeySelect } from './PluginKeySelect'

interface PluginPreviewSectionProps {
  /** The selected key; `null` when the account has none. */
  pluginKey: PluginKey | null
  pluginKeys: PluginKey[]
  onKeyChange: (keyId: string | null) => void
  /** The keys failed to load: say so instead of asking for a key. */
  keysFailed: boolean
  /** The builder is lifted over the whole window. */
  isFullscreen: boolean
  onToggleFullscreen: () => void
}

/**
 * The plugin in an iframe, built from the selected key and the builder's
 * settings. Waits for typing to settle so it doesn't reload on every key.
 */
export const PluginPreviewSection = ({
  pluginKey,
  pluginKeys,
  onKeyChange,
  keysFailed,
  isFullscreen,
  onToggleFullscreen,
}: PluginPreviewSectionProps) => {
  const { values } = useFormikContext<PluginEmbedSettings>()
  const [origin, setOrigin] = useState<string>(() =>
    pluginPreviewDefaultOrigin(window.location.hostname)
  )
  // Disabled until asked: most admins never point the preview elsewhere.
  const [editingOrigin, setEditingOrigin] = useState(false)
  const originInput = useRef<InputRef>(null)
  // A disabled input can't take focus, so focus once Edit has enabled it.
  useEffect(() => {
    if (editingOrigin) {
      originInput.current?.focus()
    }
  }, [editingOrigin])
  const { apiBaseUrl, resolved: apiBaseResolved } = useApiBaseUrl()
  const settledValues = useDebounce(values, PLUGIN_PREVIEW_DEBOUNCE_MS)
  const settledOrigin = useDebounce(origin, PLUGIN_PREVIEW_DEBOUNCE_MS)

  const preview = buildPluginPreviewUrl({
    origin: settledOrigin.trim(),
    pluginKey: pluginKey?.key ?? null,
    cfg: buildPluginCfg(settledValues),
    apiBaseUrl,
  })

  const message = () => {
    if ('url' in preview) {
      return null
    }
    if (preview.reason === 'noKey' && keysFailed) {
      return PLUGIN_KEYS_FAILED_MESSAGE
    }
    // Typed but refused by the ID rules, so it never reached the cfg.
    if (preview.reason === 'noId' && settledValues.entityId.trim()) {
      return PLUGIN_PREVIEW_MESSAGES.invalidId
    }
    return PLUGIN_PREVIEW_MESSAGES[preview.reason]
  }
  const shownMessage = message()

  return (
    <ApiAccessCard
      title={capitalizeWords(WEBSITE_PLUGIN_CARDS.preview.title)}
      subtitle={WEBSITE_PLUGIN_CARDS.preview.subtitle}
      action={
        // Escape also leaves; the shell covers the nav, so this is the visible way out.
        <Button
          size='compact'
          variant='outlined'
          icon={
            isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />
          }
          text={capitalizeWords(
            isFullscreen ? 'Exit full screen' : 'Full screen'
          )}
          onClick={onToggleFullscreen}
        />
      }
    >
      <div css={fieldsStyle}>
        {pluginKey ? (
          <div css={fieldStyle}>
            <PluginKeySelect
              pluginKey={pluginKey}
              pluginKeys={pluginKeys}
              onKeyChange={onKeyChange}
            />
          </div>
        ) : null}
        <div css={fieldStyle}>
          <TextInput
            ref={originInput}
            name='pluginPreviewUrl'
            label={capitalizeWords('Plugin URL')}
            placeholder={capitalizeWords('plugin url')}
            value={origin}
            setValue={setOrigin}
            disabled={!editingOrigin}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setEditingOrigin(false)
              }
            }}
            fieldErrors={{}}
            customError={{}}
            action={
              <Button
                variant='outlined'
                iconOnly
                icon={editingOrigin ? <CheckOutlined /> : <EditOutlined />}
                text={capitalizeWords(
                  editingOrigin ? 'Done' : 'Edit plugin URL'
                )}
                onClick={() => setEditingOrigin(!editingOrigin)}
              />
            }
          />
        </div>
      </div>
      {'url' in preview ? (
        // Held until the API base is known, so a local build doesn't load once
        // without `sn-api` and then again with it.
        apiBaseResolved ? (
          <iframe
            css={frameStyle}
            title={capitalizeWords('Website Plugin Preview')}
            src={preview.url}
            // No top navigation: the plugin can't navigate this page away.
            sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
          />
        ) : (
          <div aria-hidden='true'>
            <SkeletonRow height={PLUGIN_PREVIEW_HEIGHT} />
          </div>
        )
      ) : shownMessage ? (
        <div css={messageStyle}>
          <EmptyState
            title={capitalizeWords(shownMessage.title)}
            subtitle={shownMessage.subtitle}
            minHeight={`${PLUGIN_PREVIEW_HEIGHT}px`}
          />
        </div>
      ) : null}
      <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
        {`For the preview to load, the key's websites must include ${window.location.origin}.`}
      </Text>
    </ApiAccessCard>
  )
}

const fieldsStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

// Square: the plugin draws its own corners, so a square layout must show square.
const frameStyle = css`
  display: block;
  width: 100%;
  height: ${PLUGIN_PREVIEW_HEIGHT}px;
  border: 1px solid ${colors.SOFT_STEEL};
`

const messageStyle = css`
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 8px;
`
